// Ishod provjere za ekran "Isporuka narudžbi v4".
//
// Čiste funkcije, bez Vue-a i axios-a, da se mogu provjeriti u Nodeu. Odgovor
// GET /payment/order-group-test/user/{id} je nepravilan: ima više oblika (orders,
// orders-not-group, order-reserved-not-group, orders-reserved, order-more-restaurant),
// a ishod (već preuzeto, zamjenski obrok, STOP) se starom stranicom prepoznavao samo
// po srpskoj rečenici. Ovdje se ishod prepoznaje jednim redom: zvuk, tekst, pa oblik
// odgovora. Kad backend počne da šalje stabilan kod ishoda (status), dodaje se ovdje
// na prvo mjesto.

import { packageRows, readAddons, readPayment } from "./canteenPayment.js";

// ton = boja trake, can = ishod se može izdati (ima šta da se potvrdi)
export const KINDS = {
  ok: { tone: "ok", icon: "mdi-check-circle", can: true },
  replacement: { tone: "warn", icon: "mdi-swap-horizontal", can: true },
  taken: { tone: "taken", icon: "mdi-clock-alert-outline", can: false },
  stop: { tone: "stop", icon: "mdi-close-octagon", can: false },
  unknown: { tone: "unk", icon: "mdi-account-question-outline", can: false },
  error: { tone: "sys", icon: "mdi-wifi-off", can: false },
  session: { tone: "sys", icon: "mdi-lock-clock", can: false },
};

// zvuk kad ga server ne pošalje (isti fajlovi koje server inače šalje u polju `sound`)
const FALLBACK_SOUND = {
  replacement: "nisi-narucio-preuzmi-rezervni-obrok.mp3",
  taken: "greska-vec-preuzet-obrok.mp4",
  stop: "greska-nije-izvrsena-narudzba.mp4",
  unknown: "greska-neispravna-autentifikacija.mp4",
};

const CYR = {
  а: "a", б: "b", в: "v", г: "g", д: "d", ђ: "d", е: "e", ж: "z", з: "z", и: "i", ј: "j",
  к: "k", л: "l", љ: "lj", м: "m", н: "n", њ: "nj", о: "o", п: "p", р: "r", с: "s", т: "t",
  ћ: "c", у: "u", ф: "f", х: "h", ц: "c", ч: "c", џ: "dz", ш: "s",
};

// mala slova, bez dijakritika, ćirilica u latinicu: isti tekst se prepoznaje u oba pisma
export function fold(value) {
  return String(value == null ? "" : value)
    .toLowerCase()
    .replace(/[а-яђјљњћџ]/g, (c) => (c in CYR ? CYR[c] : c))
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/\s+/g, " ")
    .trim();
}

const TAKEN_RE = /vec preuzet/;
const REPLACEMENT_RE = /mozete preuzeti zam[je]*nsk/;
const NO_REPLACEMENT_RE = /nema zam[je]*nsk/;

export function pad2(n) {
  return String(n).padStart(2, "0");
}

export function hhmm(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return "";
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// "2026-10-19 12:09:11" (lokalno vrijeme servera) ili ISO sa Z; prazno ako se ne može pročitati
export function timeFromServer(value) {
  if (!value || typeof value !== "string") return "";
  return hhmm(new Date(value.replace(" ", "T")));
}

export function todayString(date = new Date()) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

// Šta je skenirano: QR kartica {"id":1001,"secure":"…"}, ili samo broj (čitač bez JSON-a, ručni unos).
export function parseScanned(raw) {
  const s = String(raw == null ? "" : raw).trim();
  if (!s) return { ok: false, reason: "empty" };
  if (/^\d+$/.test(s)) return { ok: true, id: s };
  try {
    const parsed = JSON.parse(s);
    const id = parsed && typeof parsed === "object" ? parsed.id : parsed;
    if (id != null && /^\d+$/.test(String(id))) return { ok: true, id: String(id) };
  } catch (e) {
    // nije JSON
  }
  return { ok: false, reason: "invalid" };
}

const first = (arr) => (Array.isArray(arr) && arr.length ? arr[0] : null);

// Jela iz odgovora. Redoslijed pretrage je isti kao na staroj stranici (testing.vue, addOrderToRecent).
export function extractMeals(d) {
  const out = { source: null, items: [], shift: null, orderedAt: "" };
  const orders = first(d.orders);
  if (orders) {
    out.source = "orders";
    out.shift = orders.shift != null ? parseInt(orders.shift, 10) || null : null;
    out.orderedAt = orders.updated_at || "";
    out.items = (orders.transaction || []).map((t) => ({
      name: (t.food && t.food.name) || "",
      quantity: t.quantity || 1,
      image: (t.food && t.food.image && t.food.image.name) || "",
      note: (t.food && t.food.translation) || "",
    }));
    return out;
  }
  const reservedOne = d["order-reserved-not-group"];
  if (reservedOne && typeof reservedOne === "object") {
    out.source = "order-reserved-not-group";
    out.items = [
      {
        name: reservedOne.name || "",
        quantity: reservedOne.quantity || 1,
        image: (reservedOne.image && reservedOne.image.name) || "",
        note: "",
      },
    ];
    return out;
  }
  const notGroup = first(d["orders-not-group"]);
  if (notGroup) {
    out.source = "orders-not-group";
    const shift = parseInt(String(notGroup.shift || "").split("-")[0], 10);
    out.shift = isNaN(shift) ? 1 : shift;
    const meal = notGroup.meals || {};
    out.items = [
      {
        name: meal.name || "",
        quantity: notGroup.quantity || 1,
        image: (meal.image && meal.image.name) || "",
        note: "",
      },
    ];
    return out;
  }
  const reserved = first(d["orders-reserved"]);
  if (reserved) {
    out.source = "orders-reserved";
    out.items = [
      {
        name: reserved.name || "",
        quantity: reserved.quantity || 1,
        image: (reserved.image && reserved.image.name) || "",
        note: "",
      },
    ];
    return out;
  }
  const more = Array.isArray(d["order-more-restaurant"]) ? d["order-more-restaurant"] : null;
  if (more && more.length) {
    out.source = "order-more-restaurant";
    out.items = more.flatMap((restaurant) =>
      packageRows(restaurant.order_guest_more_transactions).map((t) => {
        const meal = t.meals || t.food || {};
        return {
          name: meal.name || "",
          quantity: t.quantity || 1,
          image: (meal.image && meal.image.name) || "",
          note: "",
          addons: readAddons(t),
        };
      })
    );
    return out;
  }
  return out;
}

// Parametri za POST /payment/take-over (potvrda isporuke); isto kao processOrderPayment u staroj stranici.
// ctx: { userId, restaurantId, companyId, date }
export function buildTakeOverParams(d, ctx = {}) {
  const orders = first(d.orders);
  if (orders) return { order_foods_id: orders.id };
  const notGroup = first(d["orders-not-group"]);
  const reserved = first(d["orders-reserved"]);
  const reservedOne = d["order-reserved-not-group"];
  const more = d["order-more-restaurant"];
  if (!notGroup && !reserved && !reservedOne && !(Array.isArray(more) && more.length)) return null;
  const params = {
    user_id: ctx.userId,
    restaurant_id: ctx.restaurantId,
    company_id: ctx.companyId,
    shift: 1,
    date: ctx.date,
    type: 1,
  };
  if (notGroup) {
    params.meal_id = notGroup.id;
    if (notGroup.quantity) params.quantity_payed = notGroup.quantity;
  } else if (reserved) {
    params.food_id = reserved.id;
  } else if (reservedOne) {
    params.meal_id = reservedOne.id;
  } else {
    params.order_more_restaurant_id = more.map((i) => i.id);
  }
  return params;
}

/**
 * @param {Object} data  res.data.data
 * @param {Object} ctx   { code, userId, restaurantId, companyId, date } (code je skenirani ID)
 */
export function normalizeOrderGroup(data, ctx = {}) {
  const d = data && typeof data === "object" ? data : {};
  const text = typeof d.text === "string" ? d.text : "";
  const folded = fold(text);
  const serverSound = typeof d.sound === "string" && d.sound ? d.sound : "";
  const soundKey = fold(serverSound);
  const found = extractMeals(d);
  const u = d.user && typeof d.user === "object" ? d.user : null;
  const user = u
    ? {
        id: u.id,
        name: u.name || "",
        lastname: u.lastname || "",
        image_path: u.image_path || "",
        company: (Array.isArray(u.companies) && u.companies[0] && u.companies[0].name) || "",
      }
    : null;

  const takenHint = TAKEN_RE.test(folded) || soundKey.includes("vec-preuzet");
  const replacementHint =
    REPLACEMENT_RE.test(folded) || soundKey.includes("rezervni-obrok");
  const stopHint =
    NO_REPLACEMENT_RE.test(folded) ||
    soundKey.includes("nije-izvrsena-narudzba") ||
    soundKey.includes("pogresna-lokacija");
  const reservedShape =
    found.source === "order-reserved-not-group" || found.source === "orders-reserved";
  const hasItems = found.items.length > 0;

  let kind;
  if (!user) kind = "unknown";
  else if (takenHint) kind = "taken";
  else if (stopHint) kind = "stop";
  else if (hasItems && (replacementHint || (reservedShape && d.success))) kind = "replacement";
  else if (hasItems && d.success) kind = "ok";
  else kind = "stop";

  // iznos za naplatu ide uz obrok koji se izdaje; uz "Već preuzeto" i STOP ne, da se ne naplati ponovo
  const pay = KINDS[kind].can ? readPayment(d) : null;
  const userId = (user && user.id) || ctx.userId || ctx.code;
  return {
    kind,
    success: !!d.success,
    text,
    // ako je ishod stop zbog poznate rečenice, razlog se pokazuje našim tekstom; tekst servera ostaje objašnjenje
    knownReason: kind === "stop" && NO_REPLACEMENT_RE.test(folded),
    sound: serverSound || FALLBACK_SOUND[kind] || "",
    user,
    shift: found.shift,
    items: found.items,
    takenAt: kind === "taken" ? timeFromServer(found.orderedAt) : "",
    info: Array.isArray(d.info) ? d.info : [],
    pay,
    takeParams: KINDS[kind].can
      ? buildTakeOverParams(d, { ...ctx, userId })
      : null,
  };
}

// Greška zahtjeva → ishod "error" (nema veze, server, istek vremena) ili "session" (401/403).
export function classifyRequestError(err) {
  const status = err && err.response ? err.response.status : 0;
  if (status === 401 || status === 403) return { kind: "session", status };
  const timeout = !!err && (err.code === "ECONNABORTED" || err.code === "ETIMEDOUT");
  return {
    kind: "error",
    status,
    timeout,
    message: (err && err.message) || "",
  };
}
